"use client"

import { useState } from "react"
import { Container, Typography, Button, Box, Paper, Card, alpha } from "@mui/material"
import { LockOutlined } from "@mui/icons-material"
import ChangePasswordModal from "../components/ChangePasswordModal"
import { styled } from "@mui/material/styles"

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 8px 40px rgba(0, 0, 0, 0.12)",
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  },
}))

// Enhanced button styling
const StyledButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  borderRadius: theme.shape.borderRadius * 3,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  boxShadow: `0 4px 14px ${theme.palette.primary.main}40`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
  },
}))

const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(4),
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
  width: "100%",
  position: "relative",
  overflow: "hidden",
}))

const CircleDecoration = styled(Box)(({ theme, position }) => ({
  position: "absolute",
  width: 150,
  height: 150,
  borderRadius: "50%",
  background: alpha(theme.palette.common.white, 0.1),
  ...position,
}))

const SettingCard = styled(Card)(({ theme }) => ({
  transition: "transform 0.3s, box-shadow 0.3s",
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: `0 12px 20px ${alpha(theme.palette.text.primary, 0.1)}`,
  },
}))

const IconContainer = styled(Box)(({ theme, color }) => ({
  backgroundColor: alpha(color, 0.1),
  color: color,
  borderRadius: "50%",
  padding: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const AccountSettings = () => {
  const [openModal, setOpenModal] = useState(false)

  const handleOpenModal = () => setOpenModal(true)
  const handleCloseModal = () => setOpenModal(false)

  return (
    <StyledContainer maxWidth="md">
      <StyledPaper elevation={0}>
        <Box
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <LockOutlined sx={{ fontSize: "2.5rem", color: "primary.main" }} />
        </Box>

        <Typography
          variant="h4"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "0.5px",
          }}
        >
          Account Settings
        </Typography>

        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: "80%", mb: 2 }}>
          Manage your account security by changing your password
        </Typography>

        <StyledButton variant="contained" color="primary" onClick={handleOpenModal} startIcon={<LockOutlined />}>
          Change Password
        </StyledButton>
      </StyledPaper>

      <ChangePasswordModal open={openModal} onClose={handleCloseModal} />
    </StyledContainer>
  )
}

export default AccountSettings

